import { isPostingPlan } from './finishSetup.js';
import { accountBrandName, createMinimalBrand } from './quickBrand.js';
import { freePublishingState } from './freePublishing.js';

const postingWorkspaceCreates = new Map();

// TASK (2026-09-11, bare-brand-prompts #4): the gateway's server-side e-mail
// gate (EMAIL_VERIFICATION_REQUIRED) 403s EVERY onboarding/brand-profile call
// with `{ error: { code: 'EMAIL_NOT_VERIFIED' } }` once it's switched on —
// independent of the SPA's own default-OFF VerifyEmailWall flag
// (emailVerificationWallEnabled, App.jsx). RequireOnboarding used to treat
// ANY non-404 brand-profile error as "allow" (fail-open); behind this gate
// that let every account into an app it could never actually use (brand-
// profile/research/strategy all 403 the same way), and for a posting-plan
// account the auto-workspace — only ever created on a genuine 404 — never
// ran, leaving the account with zero brand profiles (staging incident,
// owner's test org, 2026-09-10). Pure classifier so the gate and its test
// share the exact same decision.
export function classifyBrandProfileError(err) {
  if (err?.status === 404) return 'missing';
  if (err?.status === 403 && err?.code === 'EMAIL_NOT_VERIFIED') return 'email_gate';
  return 'other';
}

// chatgptOrigin (owner decision 2026-09-10): a ChatGPT-origin account skips
// the interview on EVERY plan, not just Posting — it goes straight to the
// plan/card gate (RequireActiveBilling, nested right below RequireOnboarding
// in App.jsx) and gets the dashboard's FinishSetupCard as an on-demand
// reminder instead. Checked AFTER the Posting branch so a ChatGPT-origin
// Posting sign-up still gets its auto-created minimal workspace, not just a
// bare 'allow' with no brand row at all.
export function requireOnboardingDecision({ profileStatus, skipped = false, entitlements = null, chatgptOrigin = false } = {}) {
  if (profileStatus !== 404) return 'allow';
  // A personal workspace is created only by the user's own-content action.
  if (freePublishingState(entitlements)) return 'allow';
  if (isPostingPlan(entitlements)) return 'create-posting-workspace';
  if (chatgptOrigin) return 'allow';
  return skipped ? 'allow' : '/onboarding';
}

export async function resolveMissingBrand({ apiClient, user, language, skipped = false, entitlements = null, chatgptOrigin = false, onCreated } = {}) {
  const decision = requireOnboardingDecision({ profileStatus: 404, skipped, entitlements, chatgptOrigin });
  if (decision !== 'create-posting-workspace') return decision;

  const key = user?.id || user?.email || 'posting';
  if (!postingWorkspaceCreates.has(key)) {
    postingWorkspaceCreates.set(key, createMinimalBrand({
      apiClient,
      businessName: accountBrandName(user),
      language,
    }).finally(() => postingWorkspaceCreates.delete(key)));
  }

  try {
    const result = await postingWorkspaceCreates.get(key);
    await onCreated?.(result);
    return 'allow';
  } catch {
    return '/onboarding?mode=quick';
  }
}
